import type { PropsWithChildren, Ref } from 'react';

import { Box, Collapse, Fade, Grow, Slide, Zoom } from '@mui/material';

import {
  MENU_TRANSITION_FADE_ENTER_DURATION_MS,
  MENU_TRANSITION_STANDARD_DURATION_MS,
} from '@components/constants';

interface IMenuTransition {
  in?: boolean;
  position?: string;
  [others: string]: unknown;
  reference?: Ref<HTMLElement>;
  direction?: 'up' | 'down' | 'left' | 'right';
  type?: 'grow' | 'fade' | 'zoom' | 'slide' | 'collapse';
}

export const MenuTransition = ({
  reference,
  type = 'grow',
  children = null,
  direction = 'up',
  position = 'top-left',
  ...others
}: PropsWithChildren<IMenuTransition>) => {
  let positionSX;

  switch (position) {
    case 'top-right': {
      positionSX = {
        transformOrigin: 'top right',
      };
      break;
    }
    case 'top': {
      positionSX = {
        transformOrigin: 'top',
      };
      break;
    }
    case 'bottom-left': {
      positionSX = {
        transformOrigin: 'bottom left',
      };
      break;
    }
    case 'bottom-right': {
      positionSX = {
        transformOrigin: 'bottom right',
      };
      break;
    }
    case 'bottom': {
      positionSX = {
        transformOrigin: 'bottom',
      };
      break;
    }
    default: {
      positionSX = {
        transformOrigin: '0 0 0',
      };
      break;
    }
  }

  return (
    <Box ref={reference}>
      {type === 'grow' && (
        <Grow
          {...others}
          timeout={{
            appear: 0,
            exit: MENU_TRANSITION_STANDARD_DURATION_MS,
            enter: MENU_TRANSITION_STANDARD_DURATION_MS,
          }}
        >
          <Box sx={positionSX}>{children}</Box>
        </Grow>
      )}

      {type === 'collapse' && (
        <Collapse {...others} sx={positionSX}>
          {children}
        </Collapse>
      )}

      {type === 'fade' && (
        <Fade
          {...others}
          timeout={{
            appear: 0,
            exit: MENU_TRANSITION_STANDARD_DURATION_MS,
            enter: MENU_TRANSITION_FADE_ENTER_DURATION_MS,
          }}
        >
          <Box sx={positionSX}>{children}</Box>
        </Fade>
      )}

      {type === 'slide' && (
        <Slide
          {...others}
          timeout={{
            appear: 0,
            exit: MENU_TRANSITION_STANDARD_DURATION_MS,
            enter: MENU_TRANSITION_STANDARD_DURATION_MS,
          }}
          direction={direction}
        >
          <Box sx={positionSX}>{children}</Box>
        </Slide>
      )}

      {type === 'zoom' && (
        <Zoom {...others}>
          <Box sx={positionSX}>{children}</Box>
        </Zoom>
      )}
    </Box>
  );
};
